"use client";

// ============================================================
// CLUBHUB – Aufgabe anlegen oder bearbeiten
// ============================================================
// Was, wer, bis wann — mehr braucht eine Vereinsaufgabe nicht.
// Zustaendig ist eine Person aus den Kontakten, nicht nur wer einen
// Zugang hat: die meisten Helfer melden sich nie an.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import { CalendarDays, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogFooterStart,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSideAction,
  DialogSubmit,
  DialogTitle,
  FieldLabel,
  Input,
  Select,
  Textarea,
  confirmDialog,
  toast,
} from "@/components/ui";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { TaskStatus } from "@/generated/prisma/enums";
import { TASK_STATUS } from "@/lib/labels";
import { istFehler, type ActionResult } from "@/lib/action-result";
import { alsAuswahlKontakt } from "@/lib/gruppen/logik";
import type { AufgabeView } from "@/lib/pinnwand/views";
import { istIsoDatum } from "@/lib/gruppen/datum";
import { formatDate } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";

export function AufgabeDialog({
  offen,
  onClose,
  aufgabe,
  vorgabeStatus = "OPEN",
}: {
  offen: boolean;
  onClose: () => void;
  aufgabe?: AufgabeView;
  vorgabeStatus?: TaskStatus;
}) {
  return (
    <Dialog open={offen} onClose={onClose}>
      {offen && <Formular aufgabe={aufgabe} vorgabeStatus={vorgabeStatus} onClose={onClose} />}
    </Dialog>
  );
}

type Fehler = Partial<Record<"title" | "dueDate", string>>;

function Formular({ aufgabe, vorgabeStatus, onClose }: { aufgabe?: AufgabeView; vorgabeStatus: TaskStatus; onClose: () => void }) {
  const { heute, rechte, personen, aufgabeSpeichern, aufgabeLoeschen } = usePinnwand();
  const [title, setTitle] = useState(aufgabe?.title ?? "");
  const [description, setDescription] = useState(aufgabe?.description ?? "");
  const [status, setStatus] = useState<TaskStatus>(aufgabe?.status ?? vorgabeStatus);
  const [dueDate, setDueDate] = useState(aufgabe?.dueDate ?? "");
  const [assigneeId, setAssignee] = useState<string | null>(aufgabe?.assigneeId ?? null);
  const [fehler, setFehler] = useState<Fehler>({});
  const nurLesen = !rechte.schreiben;
  // Aus dem Personenbestand des Vereins (Muster oder Datenbank), in der Kurzform.
  const kontakte = useMemo(() => Object.values(personen).map(alsAuswahlKontakt), [personen]);

  async function speichern(): Promise<ActionResult<{ titel: string }>> {
    const t = title.trim();
    if (t.length < 3) return { error: "Bitte einen Titel mit mindestens drei Zeichen.", field: "title" };
    if (dueDate && !istIsoDatum(dueDate)) return { error: "Bitte ein gültiges Datum.", field: "dueDate" };
    if (!aufgabe && dueDate && dueDate < heute) return { error: "Das Datum liegt in der Vergangenheit.", field: "dueDate" };
    const r = await aufgabeSpeichern({ title: t, description: description.trim() || null, status, dueDate: dueDate || null, assigneeId }, aufgabe?.id);
    return istFehler(r) ? r : { success: true, titel: r.aufgabe.title };
  }

  async function loeschen() {
    if (!aufgabe) return;
    const ja = await confirmDialog({
      title: `„${aufgabe.title}“ löschen?`,
      description: "Erledigtes besser auf „Erledigt“ setzen — dann bleibt sichtbar, wer es gemacht hat.",
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await aufgabeLoeschen(aufgabe.id);
    if (istFehler(r)) {
      toast.error(r.error);
      return;
    }
    toast.success("Aufgabe gelöscht");
    onClose();
  }

  return (
    <DialogForm
      action={speichern}
      refresh={false}
      success={(r) => ("titel" in r ? (aufgabe ? `„${r.titel}“ gespeichert` : `„${r.titel}“ angelegt`) : null)}
      onError={(m, r) => {
        const feld = r && "field" in r ? (r.field as keyof Fehler) : undefined;
        if (feld) setFehler({ [feld]: m });
        else toast.error(m);
      }}
    >
      <DialogHeader>
        <DialogTitle>{aufgabe ? "Aufgabe" : "Neue Aufgabe"}</DialogTitle>
        <DialogDescription>
          {aufgabe ? `Angelegt von ${aufgabe.createdByName} am ${formatDate(aufgabe.createdAt)}` : "Was zu tun ist, wer sich kümmert und bis wann."}
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <Input
          name="title"
          label="Was ist zu tun?"
          required
          maxLength={140}
          autoComplete="off"
          value={title}
          disabled={nurLesen}
          onChange={(e) => {
            setTitle(e.target.value);
            setFehler((f) => ({ ...f, title: undefined }));
          }}
          placeholder="z. B. Kreide für die Linien bestellen"
          error={fehler.title}
        />
        <Textarea
          name="description"
          label="Notiz"
          rows={3}
          value={description}
          disabled={nurLesen}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Was man wissen muss, um loszulegen."
        />
        <DialogRow>
          <div className="space-y-1.5">
            <FieldLabel>Zuständig</FieldLabel>
            <KontaktAuswahl
              value={assigneeId}
              onChange={setAssignee}
              status={["ACTIVE", "PASSIVE", "HONORARY", "CONTACT"]}
              placeholder="Niemand — wer mag?"
              disabled={nurLesen}
              kontakte={kontakte}
            />
            <input type="hidden" name="assigneeId" value={assigneeId ?? ""} />
          </div>
          <Input
            type="date"
            name="dueDate"
            label="Fällig am"
            value={dueDate}
            disabled={nurLesen}
            onChange={(e) => {
              setDueDate(e.target.value);
              setFehler((f) => ({ ...f, dueDate: undefined }));
            }}
            error={fehler.dueDate}
          />
        </DialogRow>
        <Select
          name="status"
          label="Stand"
          value={status}
          onChange={(v) => setStatus(v as TaskStatus)}
          disabled={nurLesen}
          options={Object.values(TaskStatus).map((s) => ({ value: s, label: TASK_STATUS[s].label }))}
        />
        {aufgabe?.eventTitle && (
          <DialogNotice
            icon={<CalendarDays className="text-text-tertiary h-4 w-4" />}
            action={
              <Link href={aufgabe.eventId ? `/kalender/${aufgabe.eventId}` : "/kalender"} className="text-caption text-accent hover:text-accent-hover font-medium">
                Zum Termin
              </Link>
            }
          >
            Gehört zum Termin „{aufgabe.eventTitle}“.
          </DialogNotice>
        )}
      </DialogContent>
      <DialogFooter>
        {aufgabe && rechte.schreiben && (
          <DialogFooterStart>
            <DialogSideAction tone="danger" onClick={() => void loeschen()}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Löschen
            </DialogSideAction>
          </DialogFooterStart>
        )}
        <DialogCancel>{nurLesen ? "Schließen" : "Abbrechen"}</DialogCancel>
        {!nurLesen && <DialogSubmit>{aufgabe ? "Speichern" : "Anlegen"}</DialogSubmit>}
      </DialogFooter>
    </DialogForm>
  );
}
